import React, { memo } from 'react';
import brand from './brand';
import './QuadrantLoader.css';

// The product's name, drawn as the brand draws it, animated once.
//
// It replaced an animated picture of the same word: 3.5MB that had to be fetched before anybody
// could read the name, sharp only at the size it was exported, and carrying its own navy background
// so the name could never sit on anything else.
//
// The letterforms are the real ones - outlines taken from the brand artwork - rather than a
// geometric font that is nearly right, because nearly right reads as a lookalike rather than as the
// logo. They arrive with the brand this address was handed, like every other name here: a drawing of
// a product's name is still that product's name, and the bundle every address shares must not carry
// one.
//
// Composition follows the animation it replaces: the wordmark, and beneath it a capability line held
// between corner brackets with a mark, cycling through what the product does. Where there is a real
// loading state it displaces the cycle and says that instead, because a loader advertising at
// somebody who is waiting is decoration pretending to be progress.
let instance = 0;

// How long one capability holds before the next replaces it, cross-fade included. Taken from the
// animation this replaces, where three of them fill about eight seconds.
const HOLD_MS = 2850;

// The mark that goes with a capability, drawn from the geometry the brand handed this address.
//
// Solid artwork rather than the line icons that stood in for it: these are the drawings the product
// is actually sold under, and a near-enough icon beside a real wordmark reads as a stand-in.
//
// Each one moves in the way the thing it depicts moves - a wheel turns, a tired driver's head
// drops and recovers, a loaded body settles, a truck on speed lines pulls away. Movement that
// illustrates is worth the few bytes; movement for its own sake on a login page is not.
const Mark = ({ name }) => {
  const mark = brand.marks && brand.marks[name];
  if (!mark) return null;
  return (
    <svg
      className={`quadrant-loader__mark quadrant-loader__mark--${name}`}
      viewBox={`0 0 ${mark.box} ${mark.box}`}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      {mark.shapes.map((shape, i) => (shape.c ? (
        // eslint-disable-next-line react/no-array-index-key
        <circle key={i} cx={shape.c[0]} cy={shape.c[1]} r={shape.c[2]} />
      ) : (
        // eslint-disable-next-line react/no-array-index-key
        <path key={i} d={shape.d} />
      )))}
    </svg>
  );
};

const QuadrantLoader = memo(({
  word, message, leaving = false, full = false,
}) => {
  const text = word || brand.name;
  const art = brand.wordmarkPaths;
  // A real loading state displaces the cycle. While something is actually happening the line says
  // what, rather than carrying on advertising over the top of it.
  const capabilities = (!message && brand.capabilities) || [];
  const [shown, setShown] = React.useState(0);

  React.useEffect(() => {
    if (capabilities.length < 2) return undefined;
    // Somebody who asked for less movement gets the first capability and no rotation: a line that
    // rewrites itself is movement whether or not it slides.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const timer = setInterval(() => setShown((n) => (n + 1) % capabilities.length), HOLD_MS);
    return () => clearInterval(timer);
  }, [capabilities.length]);

  // The maker's name, which the word turns into and back out of, once.
  //
  // It shares "Quadra" with the product's name to the pixel, so the shared glyphs are drawn once and
  // only the tail changes. The box has to be wide enough for the longer of the two words; each state
  // is then centred in it by shifting the whole word, which is why the letters drift as the tail
  // grows rather than the word jumping.
  const maker = art?.maker;
  const shared = maker ? maker.from : (art?.glyphs?.length ?? 0);
  const boxWidth = maker ? maker.width : art?.width;
  // The product's own name, centred in that box.
  const shift = maker ? (maker.width - art.width) / 2 : 0;
  // The maker's lockup is a quarter wider than the box, because its mark sits out to the left of
  // the first letter. Letting it hang outside put it off the left edge of the screen in the desktop
  // sidebar, so the state is fitted to the box instead: the letters draw back about a fifth while
  // the mark is up, which is a move with a reason - the name pulling back to show who makes it -
  // and leaves nothing painting outside its slot at any width.
  const makerScale = maker ? boxWidth / (maker.width - maker.markX) : 1;
  const makerTransform = maker
    ? `translate(${(-maker.markX * makerScale).toFixed(2)}px, `
      + `${(((art.height - (art.height - maker.markY) * makerScale) / 2) - maker.markY * makerScale).toFixed(2)}px) `
      + `scale(${makerScale.toFixed(5)})`
    : 'none';

  const current = capabilities[shown % capabilities.length] || null;
  const line = message || current?.label || brand.tagline || '';
  // Ids must not collide when the loader appears twice on one page.
  // eslint-disable-next-line no-plusplus
  const uid = React.useMemo(() => `qw${++instance}`, []);

  return (
    <div
      className={`quadrant-loader${full ? ' quadrant-loader--full' : ''}${leaving ? ' quadrant-loader--leaving' : ''}`}
      // The drawing is decoration. A screen reader is told the name once, as text, and the loading
      // state through the live region - never read eight separate glyphs.
      role={message ? 'status' : undefined}
      aria-live={message ? 'polite' : undefined}
      aria-busy={message ? true : undefined}
    >
      {art?.glyphs?.length ? (
        <svg
          className="quadrant-loader__art"
          viewBox={`0 0 ${boxWidth} ${art.height}`}
          role="img"
          aria-label={text}
          focusable="false"
        >
          <defs>
            {/* One gradient across the whole word, in the drawing's own coordinates - so it flows
                through the letters rather than restarting at each one. */}
            <linearGradient id={`${uid}-ink`} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={boxWidth} y2={art.height}>
              <stop offset="0%" stopColor="#3ab8e2" />
              <stop offset="45%" stopColor="#3189c4" />
              <stop offset="100%" stopColor="#1d2b5e" />
            </linearGradient>
            {/* A narrow band, not a wash. Spread over half the word's width this stopped reading as
                light crossing the letters and started reading as the logo fading out and back -
                every glyph pale at once. Kept to about a sixth of the width so there is always
                finished wordmark on both sides of it. */}
            <linearGradient id={`${uid}-sheen`} gradientUnits="objectBoundingBox" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#fff" stopOpacity="0" />
              <stop offset="42%" stopColor="#fff" stopOpacity="0" />
              <stop offset="50%" stopColor="#fff" stopOpacity="0.55" />
              <stop offset="58%" stopColor="#fff" stopOpacity="0" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            {/* The light pass is clipped to the glyphs, so it travels through the letters and never
                across the space between them. */}
            {/* Only the product's own letters. The light passes long before the maker's name
                arrives, and a clip that included those letters would show the band in the shape of
                words nobody can see yet. */}
            <clipPath id={`${uid}-clip`}>
              {art.glyphs.map((d, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <path key={i} d={d} fillRule="evenodd" />
              ))}
            </clipPath>
          </defs>

          {/* Everything sits in one group so the shift that centres each state carries the clip
              and the light with it. */}
          <g
            className="quadrant-loader__shift"
            style={{ '--shift': `${shift}px`, '--maker-transform': makerTransform }}
          >
            {/* The letters both words have in common, drawn once and never touched by the change. */}
            {art.glyphs.slice(0, shared).map((d, i) => (
              <g
                // The glyph list is fixed and never reordered, which is the case where an index key
                // is the right one.
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className="quadrant-loader__glyph"
                style={{ '--i': i }}
              >
                <path d={d} fill={`url(#${uid}-ink)`} fillRule="evenodd" />
              </g>
            ))}

            <g className="quadrant-loader__tail quadrant-loader__tail--product">
              {art.glyphs.slice(shared).map((d, i) => (
                <g
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className="quadrant-loader__glyph"
                  style={{ '--i': shared + i }}
                >
                  <path d={d} fill={`url(#${uid}-ink)`} fillRule="evenodd" />
                </g>
              ))}
            </g>

            {maker && (
              <g className="quadrant-loader__tail quadrant-loader__tail--maker">
                {maker.glyphs.map((d, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <path key={i} d={d} fill={`url(#${uid}-ink)`} fillRule="evenodd" />
                ))}
                {/* The mark, where the artwork puts it relative to the first letter.
                    Position and movement are on separate groups on purpose: in SVG the transform
                    attribute and the CSS transform property are one property, so an animation that
                    sets a transform silently replaces the translate that places the thing - which
                    drew the mark on top of the Q instead of beside it. */}
                <g transform={`translate(${maker.markX} ${maker.markY})`}>
                  <g className="quadrant-loader__maker-mark">
                    {maker.mark.map((d, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <path key={i} d={d} fill={`url(#${uid}-ink)`} fillRule="evenodd" />
                    ))}
                  </g>
                </g>
              </g>
            )}

            <g clipPath={`url(#${uid}-clip)`}>
              <rect
                className="quadrant-loader__sheen"
                x={-art.width}
                y="0"
                width={art.width}
                height={art.height}
                fill={`url(#${uid}-sheen)`}
              />
            </g>
          </g>
        </svg>
      ) : (
        // No drawing for this brand: its name, set in type, which is what it had before.
        <p className="quadrant-loader__word">{text}</p>
      )}

      {!!line && (current ? (
        <div className="quadrant-loader__line" style={{ '--hold': `${HOLD_MS}ms` }}>
          {/* The mark sits on a tint of its own colour rather than between corner brackets. The
              brackets came from the animation this replaces, where they framed a mark four times
              this size; here they were two stray ticks with a gap between them. */}
          <span className="quadrant-loader__tile" aria-hidden="true">
            <span key={`mark-${shown}`} className="quadrant-loader__swap">
              <Mark name={current.icon} />
            </span>
          </span>

          <span className="quadrant-loader__text">
            <span
              key={`label-${shown}`}
              className="quadrant-loader__caption quadrant-loader__swap"
              // A rotation is a pitch, not a status. Announcing each turn would interrupt somebody
              // every few seconds for no news; the capabilities are read once, below, instead.
              aria-hidden="true"
            >
              {line}
            </span>

            {/* One segment per capability, the current one filling over exactly the time it has
                left. The rotation is a sequence of three, so the page says so - and a rule that is
                a real clock tells somebody that this will change, and when, rather than leaving a
                phrase to swap out from under them. */}
            {capabilities.length > 1 && (
              <span className="quadrant-loader__progress" aria-hidden="true">
                {capabilities.map((capability, i) => (
                  <span
                    key={capability.icon}
                    className={`quadrant-loader__segment${i === shown ? ' is-current' : ''}`}
                  >
                    {i === shown && <span key={`fill-${shown}`} className="quadrant-loader__fill" />}
                  </span>
                ))}
              </span>
            )}

            {/* The capabilities as one sentence, for a screen reader, since the visible line
                rotates and is not announced. */}
            <span className="quadrant-loader__reader">{capabilities.map((c) => c.label).join('. ')}</span>
          </span>
        </div>
      ) : (
        // A real loading state, or a brand with a strapline and nothing to rotate: one line of
        // text, centred under the name, with none of the apparatus the rotation needs.
        <p className="quadrant-loader__line quadrant-loader__line--plain">
          <span className="quadrant-loader__caption">{line}</span>
        </p>
      ))}
    </div>
  );
});

export default QuadrantLoader;
